import React from 'react'
import { Box, Flex, PrimaryButton, Text } from '@moonpig/launchpad-components'
import { system as s } from '@moonpig/launchpad-system'
import { styled, css } from '@moonpig/launchpad-utils'
import { IconSystemFilter } from '@moonpig/launchpad-assets'
import {
  FILTER_COUNT_SIZE_PX,
  FILTERS_MENU_BUTTON_HEIGHT_PX,
} from '../../../FiltersCarousel/constants'
import { useFindLocaleText } from '../../../../text-localisation'

const styledToggleVariantStyles = css<{
  filtersApplied: boolean
}>`
  transition: background-color 100ms ease-in-out, border-color 100ms ease-in-out,
    color 100ms ease-in-out;

  ${({ filtersApplied }) =>
    s({
      bgcolor: 'colorBackground01',
      border: 2,
      px: 6,
      py: '10px',
      mr: 3,
      borderColor: filtersApplied
        ? 'colorInteractionSelectedState'
        : 'colorBlack20',
      color: filtersApplied
        ? 'colorInteractionSelectedState'
        : 'colorInteractionIcon',
      boxShadow: 0,
      borderRadius: 2,
      minHeight: FILTERS_MENU_BUTTON_HEIGHT_PX,
    })}

  &:not(:disabled) {
    &:hover {
      text-decoration: none;
      outline: none;

      ${({ filtersApplied }) =>
        s({
          boxShadow: 0,
          borderColor: filtersApplied
            ? 'colorInteractionSelectedState'
            : 'colorBlack30',
          bgcolor: filtersApplied
            ? 'colorBackgroundSelectedState'
            : 'colorBlack05',
        })}
    }

    &:focus {
      text-decoration: none;
      outline: none;
      ${({ filtersApplied }) =>
        s({
          boxShadow: 5,
          borderColor: filtersApplied
            ? 'colorInteractionSelectedState'
            : 'colorBlack30',
          bgcolor: filtersApplied
            ? 'colorBackgroundSelectedState'
            : 'colorBlack05',
        })}
    }
  }
`

const StyledToggleWrapper = styled(Flex)`
  ${s({
    p: 0,
    borderRight: 0,
    borderColor: 'colorBlack10',
  })}
`

const StyledToggle = styled(PrimaryButton)<{
  filtersApplied: boolean
}>`
  ${styledToggleVariantStyles}
`

const StyledFilterCount = styled.div`
  width: ${FILTER_COUNT_SIZE_PX};
  height: ${FILTER_COUNT_SIZE_PX};
  ${s({
    borderRadius: 7,
    bgcolor: 'colorInteractionSelectedState',
    color: 'colorTextInverted',
    typography: 'typeButtonLabel',
  })}
`

type ToggleProps = {
  onClick: () => void
  appliedFiltersCount: number
  isOpen: boolean
  ref?: React.Ref<HTMLAnchorElement>
}

export const Toggle = React.forwardRef<HTMLAnchorElement, ToggleProps>(
  ({ onClick, appliedFiltersCount, isOpen }, ref) => {
    const localiseText = useFindLocaleText()

    const filtersApplied = appliedFiltersCount > 0

    const createLeadingIcon = () => {
      return filtersApplied ? (
        <StyledFilterCount
          aria-label={localiseText('find.n_filters_applied', {
            filterCount: appliedFiltersCount,
          })}
        >
          {appliedFiltersCount}
        </StyledFilterCount>
      ) : (
        <Box data-testid={'filters-menu-icon'}>
          <IconSystemFilter aria-hidden width="24px" />
        </Box>
      )
    }

    return (
      <StyledToggleWrapper>
        <StyledToggle
          ref={ref}
          aria-label={localiseText('find.open_filters')}
          aria-expanded={isOpen}
          aria-controls="filters-menu"
          data-testid="filters-menu-toggle"
          onClick={onClick}
          leadingIcon={createLeadingIcon()}
          filtersApplied={filtersApplied}
        >
          <Text typography="typeButtonLabel">
            {localiseText('find.filters')}
          </Text>
        </StyledToggle>
      </StyledToggleWrapper>
    )
  },
)
